import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { path } from 'ramda';

import { TextInput } from 'components';
import { debounce } from 'utils/common';
import classNames from 'classnames/bind';
import {
  fetchRegions,
  fetchStreets,
  fetchHouses,
} from '../../../../actions';
import { SET_ALLINONE_VALUE } from '../../../../constants';
import styles from './styles.pcss';

const cx = classNames.bind(styles);

class Dropdown extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      inputRef: null,
      focus: false,
      value: '',
      activeEntry: null,
    };

    const delay = props.type === 'house' ? 0 : 200;

    this.handleUpdateInput = debounce(this.handleUpdateInput.bind(this), delay);
  }

  componentDidMount() {
    document.addEventListener('click', this.handleOuterClick);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleOuterClick);
  }

  handleOuterClick = (event) => {
    const {
      setValue,
      entities,
      resetValue,
    } = this.props;
    let { value } = this.state;

    if (event.target !== this.state.inputRef && this.state.focus) {
      if (value) {
        if (entities && entities.length) {
          value = entities[0].label;
          setValue(entities[0]);
        } else {
          resetValue(value);
        }
      } else {
        resetValue();
      }
      this.setState({
        focus: false,
        value,
      });
    }
  };

  handleGetRef = (inputRef) => {
    this.setState({
      inputRef,
    });
  };

  handleFocus = () => {
    this.setState({
      focus: true,
    });
  };

  handleKeyDown = (event) => {
    const { activeEntry } = this.state;
    const { entities } = this.props;

    if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) {
      return;
    }

    event.preventDefault();

    if (event.key === 'ArrowDown') {
      let newActiveEntry = activeEntry + 1;
      if (entities && newActiveEntry > entities.length) {
        newActiveEntry = entities.length;
      }
      this.setState({ activeEntry: newActiveEntry });
    }

    if (event.key === 'ArrowUp') {
      const newActiveEntry = activeEntry - 1 >= 0 ? activeEntry - 1 : 0;
      this.setState({ activeEntry: newActiveEntry });
    }

    if (event.key === 'Enter' && activeEntry) {
      this.handleSelect(entities[activeEntry - 1]);
    }
  };

  handleSelect = (entity) => {
    const { setValue, onChange } = this.props;
    onChange(entity, true);
    this.setState({
      focus: false,
      value: entity.label,
      activeEntry: null,
    }, () => { setValue(entity); });
  };

  handleUpdateInput(value = '') {
    const {
      type, region, street, onChange, actions,
    } = this.props;

    this.setState({ value });

    if (type === 'region') {
      actions.fetchRegions(value);
    }

    if (type === 'street') {
      actions.fetchStreets(region.id, value);
    }

    if (type === 'house') {
      actions.fetchHouses(street.id, value);
    }

    if (onChange) {
      onChange(value);
    }
  }

  render() {
    const {
      type,
      disabled,
      entities,
      value,
      status,
      size,
    } = this.props;
    const { activeEntry } = this.state;
    return (
      <div className={cx('layout')}>
        <TextInput
          name={type}
          value={typeof value === 'undefined' ? this.state.value : value}
          onChange={this.handleUpdateInput}
          onClear={this.handleUpdateInput}
          onKeyDown={this.handleKeyDown}
          onFocus={this.handleFocus}
          getRef={this.handleGetRef}
          status={status}
          size={size}
          disabled={disabled}
          autoComplete="off"
          clearable
          ref={this.props.inputRef}
        />
        {entities.length ? (
          <ul className={cx('dropdown', { focus: this.state.focus })}>
            {entities.map((entity, index) => (
              <li
                key={index}
                className={cx('dropdownItem', { activeEntry: index === activeEntry - 1 })}
                onClick={() => { this.handleSelect(entity); }}
                role="presentation"
              >
                {entity.label}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  }
}

Dropdown.defaultProps = {
  region: {},
  street: {},
  entities: [],
  disabled: false,
  status: 'ok',
  size: 'default',
};

Dropdown.propTypes = {
  type: PropTypes.oneOf([
    'region',
    'street',
    'house',
  ]).isRequired,
  size: PropTypes.oneOf(['default', 'big']),
  setValue: PropTypes.func.isRequired,
  resetValue: PropTypes.func.isRequired,
  onChange: PropTypes.func,
  entities: PropTypes.array,
  region: PropTypes.object,
  street: PropTypes.object,
  disabled: PropTypes.bool,
  value: PropTypes.string,
  inputRef: PropTypes.func,
  status: PropTypes.string,
  actions: PropTypes.object,
};

const mapStateToProps = ({ home }, props) => {
  // reSelect
  const disabled = props.type === 'street'
   && (!path(['allInOne', 'region', 'id'], home) || path(['allInOne', 'tariffNotFoundInRegion'], home))
   || props.type === 'house' && !path(['allInOne', 'street', 'id'], home);

  return {
    ...home.allInOne,
    disabled,
    entities: path(['allInOne', `${props.type}s`], home),
  };
};

const mapDispatchToProps = (dispatch, props) => ({
  setValue: (entity) => {
    let payload = {};
    switch (props.type) {
      case 'region':
        payload = {
          regions: [entity],
        };
        break;
      case 'street':
        payload = {
          streets: [entity],
        };
        break;
      case 'house':
        payload = {
          houses: [entity],
        };
        break;
      default:
        throw new Error(`There is no implementation for type ${props.type}`);
    }

    dispatch({
      type: SET_ALLINONE_VALUE,
      resetError: true,
      payload: {
        [props.type]: entity,
        [`${props.type}IsNotFound`]: false,
        ...payload,
      },
    });
  },
  resetValue: (value = '') => {
    const streetPayload = {
      street: {},
      streets: [],
    };

    const regionPayload = {
      region: {},
      regions: [],
    };

    let payload = {
      [`${props.type}IsNotFound`]: !!value,
      house: {},
      houses: [],
      flat: '',
    };

    switch (props.type) {
      case 'region':
        payload = { ...payload, ...regionPayload, ...streetPayload };
        break;
      case 'street':
        payload = { ...payload, ...streetPayload };
        break;
      case 'house':
        break;
      default:
        throw new Error(`There is no implementation for type ${props.type}`);
    }
    dispatch({
      type: SET_ALLINONE_VALUE,
      payload,
    });
  },
  actions: bindActionCreators({
    fetchRegions,
    fetchStreets,
    fetchHouses,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(Dropdown);
